import { PENDO_API_KEY } from './config';

const PENDO_SCRIPT_ID = 'pendo-agent-script';
let pendoScriptPromise = null;
const PENDO_AGENT_SRC = `https://cdn.pendo.io/agent/static/${PENDO_API_KEY}/pendo.js`;

const resetPendoScriptPromise = () => {
    pendoScriptPromise = null;
};

const getPendoLoadError = () => new Error('Failed to load Pendo script');

const isTrustedPendoScript = (existingScript) =>
    existingScript.tagName === 'SCRIPT' &&
    existingScript.src === PENDO_AGENT_SRC;

const replacePendoScript = (existingScript) => {
    const replacementScript = document.createElement('script');
    replacementScript.id = PENDO_SCRIPT_ID;
    replacementScript.src = PENDO_AGENT_SRC;
    replacementScript.async = true;
    existingScript.replaceWith(replacementScript);
    return replacementScript;
};

const waitForScriptLoad = (script) =>
    new Promise((resolve, reject) => {
        script.addEventListener(
            'load',
            () => {
                script.setAttribute('data-pendo-loaded', 'true');
                if (window.pendo) {
                    resolve(true);
                    return;
                }
                reject(getPendoLoadError());
            },
            { once: true },
        );
        script.addEventListener('error', () => reject(getPendoLoadError()), {
            once: true,
        });
    });

const loadPendoScript = () => {
    if (window.pendo) {
        return Promise.resolve(true);
    }

    if (pendoScriptPromise) {
        return pendoScriptPromise;
    }

    pendoScriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.getElementById(PENDO_SCRIPT_ID);
        if (existingScript) {
            if (window.pendo) {
                resolve(true);
                return;
            }

            // If script already settled without exposing window.pendo, replace it so
            // callers can retry instead of waiting forever on non-firing events.
            const shouldReplaceScript =
                !isTrustedPendoScript(existingScript) ||
                Boolean(existingScript.dataset.pendoLoaded);
            const scriptToWaitOn = shouldReplaceScript
                ? replacePendoScript(existingScript)
                : existingScript;

            waitForScriptLoad(scriptToWaitOn)
                .then(resolve)
                .catch(reject);
            return;
        }

        const script = document.createElement('script');
        script.id = PENDO_SCRIPT_ID;
        script.src = PENDO_AGENT_SRC;
        script.async = true;
        waitForScriptLoad(script)
            .then(resolve)
            .catch(reject);
        document.body.appendChild(script);
    });

    pendoScriptPromise.catch(resetPendoScriptPromise);

    return pendoScriptPromise;
};

export const clearPendoSession = () => {
    if (!window.pendo) {
        return;
    }

    if (typeof window.pendo.clearSession === 'function') {
        window.pendo.clearSession();
    }
};

export const stopPendoTracking = () => {
    if (!window.pendo) {
        return;
    }

    clearPendoSession();

    if (typeof window.pendo.teardown === 'function') {
        window.pendo.teardown();
    }

    // Ensure next login gets a clean bootstrap without requiring a hard refresh.
    delete window.pendo;

    const existingScript = document.getElementById(PENDO_SCRIPT_ID);
    if (existingScript) {
        existingScript.remove();
    }

    resetPendoScriptPromise();
};

export const syncPendoIdentity = ({ visitor, account, hasInitialized }) => {
    if (!window.pendo) {
        return hasInitialized;
    }

    const payload = { visitor, account };

    if (!hasInitialized) {
        window.pendo.initialize(payload);
        return true;
    }

    if (typeof window.pendo.updateOptions === 'function') {
        window.pendo.updateOptions(payload);
        return true;
    }

    if (typeof window.pendo.identify === 'function') {
        window.pendo.identify(payload);
        return true;
    }

    window.pendo.initialize(payload);
    return true;
};

export default loadPendoScript;
